"use client";

import { SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import type { GuideMetadata } from "@/lib/guides";
import { BrowseByNeed, type GuideNeedSelection } from "./BrowseByNeed";
import { GuideCard } from "./GuideCard";
import { GuidesEmptyState } from "./GuidesEmptyState";
import { GuidesHero } from "./GuidesHero";

type GuidesLibraryClientProps = {
  guides: GuideMetadata[];
  featuredGuides: GuideMetadata[];
  filters: string[];
};

export function GuidesLibraryClient({ guides, featuredGuides, filters }: GuidesLibraryClientProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(filters[0] ?? "All");
  const hasGuides = guides.length > 0;

  const filteredGuides = useMemo(() => {
    return guides.filter((guide) => matchesFilter(guide, selectedFilter) && matchesSearch(guide, searchTerm));
  }, [guides, searchTerm, selectedFilter]);

  function handleNeedSelect(selection: GuideNeedSelection) {
    setSelectedFilter(selection.filter ?? "All");
    setSearchTerm(selection.search ?? "");
    document.getElementById("guides-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <>
      <GuidesHero guideCount={guides.length} searchTerm={searchTerm} onSearchTermChange={setSearchTerm} />
      <BrowseByNeed hasGuides={hasGuides} onSelectNeed={handleNeedSelect} />

      {hasGuides ? (
        <>
          {featuredGuides.length > 0 ? (
            <section className="bg-[#FBFEFA] px-4 py-12 text-slate-950 sm:px-6 sm:py-14 lg:px-8" aria-labelledby="featured-guides-heading">
              <div className="mx-auto max-w-[1200px]">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-sm font-black text-[#087D2F]">Featured guides</p>
                    <h2 id="featured-guides-heading" className="mt-2 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">
                      Start with the strongest resources
                    </h2>
                  </div>
                  <p className="max-w-xl text-base font-medium leading-7 text-slate-600">
                    Prioritized guides for the most common Moroccan English planning jobs.
                  </p>
                </div>

                <div className="mt-8 grid gap-5 lg:grid-cols-3">
                  {featuredGuides.map((guide) => (
                    <GuideCard key={guide.slug} guide={guide} featured />
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          <section
            id="guides-results"
            className="bg-white px-4 py-12 text-slate-950 sm:px-6 sm:py-14 lg:px-8"
            aria-labelledby="all-guides-heading"
          >
            <div className="mx-auto max-w-[1200px]">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <p className="inline-flex items-center gap-2 text-sm font-black text-[#087D2F]">
                    <SlidersHorizontal className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                    Search and filter
                  </p>
                  <h2 id="all-guides-heading" className="mt-2 text-3xl font-black leading-tight text-slate-950 sm:text-4xl">
                    All guides
                  </h2>
                  <p className="mt-3 text-base font-medium leading-7 text-slate-600">
                    {filteredGuides.length} of {guides.length} guides shown
                  </p>
                </div>

                <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-2 lg:mx-0 lg:max-w-[720px] lg:flex-wrap lg:justify-end lg:overflow-visible lg:px-0 lg:pb-0">
                  {filters.map((filter) => {
                    const isSelected = selectedFilter === filter;
                    return (
                      <button
                        key={filter}
                        type="button"
                        onClick={() => setSelectedFilter(filter)}
                        className={
                          isSelected
                            ? "shrink-0 rounded-full bg-[#08A62F] px-4 py-2 text-sm font-black text-white shadow-[0_14px_30px_-22px_rgba(8,166,47,0.9)]"
                            : "shrink-0 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-600 transition-colors duration-150 hover:border-green-200 hover:bg-green-50 hover:text-[#087D2F]"
                        }
                        aria-pressed={isSelected}
                      >
                        {filter}
                      </button>
                    );
                  })}
                </div>
              </div>

              {filteredGuides.length > 0 ? (
                <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredGuides.map((guide) => (
                    <GuideCard key={guide.slug} guide={guide} />
                  ))}
                </div>
              ) : (
                <div className="mt-8 rounded-[24px] border border-dashed border-green-200 bg-green-50/70 px-5 py-10 text-center">
                  <h3 className="text-xl font-black text-slate-950">No guides found.</h3>
                  <p className="mt-2 text-base font-medium leading-7 text-slate-600">
                    Try another level, skill, or topic.
                  </p>
                </div>
              )}
            </div>
          </section>
        </>
      ) : (
        <GuidesEmptyState />
      )}
    </>
  );
}

function matchesFilter(guide: GuideMetadata, selectedFilter: string): boolean {
  if (selectedFilter === "All") {
    return true;
  }

  return guideSearchText(guide).includes(normalize(selectedFilter));
}

function matchesSearch(guide: GuideMetadata, searchTerm: string): boolean {
  const normalizedSearch = normalize(searchTerm);
  if (!normalizedSearch) {
    return true;
  }

  return guideSearchText(guide).includes(normalizedSearch);
}

function guideSearchText(guide: GuideMetadata): string {
  return normalize(
    [
      guide.title,
      guide.shortTitle,
      guide.description,
      guide.category,
      ...guide.level,
      ...guide.topic,
      ...guide.audience,
      guide.bestFor,
    ]
      .filter(Boolean)
      .join(" "),
  );
}

function normalize(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}
